import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  AbstractControl,
  FormControl,
  ReactiveFormsModule,
} from '@angular/forms';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { BehaviorSubject, Subject } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  switchMap,
  tap,
  finalize,
  takeUntil,
} from 'rxjs/operators';
import { LoaderService } from '../../core/services/loader.service';
import { LookupItem } from '../../core/models/lookup.models';
import { LookupService } from '../../core/services/lookup.service';
import { LoadingContentComponent } from '../../shared/loading-content/loading-content.component';
import { SecurityService } from '../../core/services/security.service';

@Component({
  selector: 'app-search-select',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NzFormModule,
    NzSelectModule,
    LoadingContentComponent,
  ],
  template: `
    <nz-form-item>
      <nz-form-label [nzFor]="inputId">{{ label }}</nz-form-label>
      <nz-form-control nzHasFeedback [nzErrorTip]="errorTpl">
        <nz-select
          [id]="inputId"
          nzSuffixIcon
          nzShowSearch
          nzAllowClear
          [formControl]="control"
          [nzDisabled]="isDisabled"
          [nzNotFoundContent]="loadingContent.loadingTemplate"
          [nzPlaceHolder]="currentPlaceholder"
          [class.has-parent-org]="hasParentOrg"
          [nzOptionOverflowSize]="optionOverflowSize"
          (nzOnSearch)="onSearch($event)">
          <nz-option
            *ngFor="let option of options$ | async"
            [nzLabel]="option.value"
            [nzValue]="option.id">
          </nz-option>
        </nz-select>

        <app-loading-content
          #loadingContent
          [isLoading$]="loading$"
          [noDataText]="noDataText">
        </app-loading-content>

        <ng-template #errorTpl let-control>
          <ng-container *ngIf="control.hasError('required')">
            {{ requiredErrorMessage }}
          </ng-container>
        </ng-template>
      </nz-form-control>
    </nz-form-item>
  `,
})
export class SearchSelectComponent implements OnInit, OnDestroy {
  @Input() label = '';
  @Input() inputId = '';
  @Input() placeholder = 'Search';
  @Input() noDataText = 'No data found';
  @Input() requiredErrorMessage = 'This field is required';
  @Input() lookupType = '';
  @Input() debounceTimeMs = 300;
  @Input() isEditMode = false;
  @Input() isParentOrganization = false;
  @Input() currentValue: string | undefined = '';
  @Input() isViewMode = false;
  @Input() optionOverflowSize = 7;
  @Input() defaultOption?: LookupItem;
  @Input() useDefaultProactiveStrategy = false;
  @Output() optionSelected = new EventEmitter<LookupItem | null>();

  defaultOptions = [
    { id: 71, value: 'Replacement Behavior' },
    { id: 72, value: 'Proactive Strategy' },
    { id: 73, value: 'Reactive Strategy' },
  ];

  @Input()
  get control(): FormControl {
    return this._control;
  }
  set control(value: AbstractControl | null) {
    this._control = value as FormControl;
  }
  private _control!: FormControl;

  private readonly loaderService = inject(LoaderService);
  private readonly lookupService = inject(LookupService);
  private readonly securityService = inject(SecurityService);
  private readonly searchSubject = new Subject<string>();
  private readonly destroy$ = new Subject<void>();

  options$ = new BehaviorSubject<LookupItem[]>([]);
  loading$ = this.loaderService.loading$;

  get isDisabled(): boolean {
    return this.isEditMode && this.isParentOrganization;
  }

  get currentPlaceholder(): string {
    return this.currentValue || this.placeholder;
  }

  get hasParentOrg(): boolean {
    return !!this.currentValue && !this.isViewMode;
  }

  ngOnInit(): void {
    this.setupSearchSubscription();
    this.setupControlValueSubscription();
    if (this.lookupType === 'StrategyType') {
      this.options$.next(this.defaultOptions);
      if (this.defaultOption) {
        this.control.setValue(this.defaultOption.id);
      }
    }
    if (this.lookupType === 'StrategyType') {
      this.options$.next(this.defaultOptions);

      if (this.defaultOption?.id) {
        this.control.setValue(this.defaultOption.id);
      } else if (this.useDefaultProactiveStrategy && !this.control.value) {
        const proactiveStrategy = this.defaultOptions.find(
          option => option.value === 'Proactive Strategy'
        );
        if (proactiveStrategy) {
          this.control.setValue(proactiveStrategy.id);
        }
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSearch(searchTerm: string): void {
    this.searchSubject.next(searchTerm);
  }

  private setupSearchSubscription(): void {
    this.searchSubject
      .pipe(
        takeUntil(this.destroy$),
        tap(() => {
          this.loaderService.show();
          this.options$.next([]);
        }),
        debounceTime(this.debounceTimeMs),
        distinctUntilChanged(),
        map(searchTerm => this.validateAndSanitizeSearchTerm(searchTerm)),
        filter((searchTerm): searchTerm is string => searchTerm !== null),
        switchMap(searchTerm =>
          this.lookupService.getLookupValue(this.lookupType, searchTerm).pipe(
            finalize(() => {
              this.loaderService.hide();
            })
          )
        )
      )
      .subscribe({
        next: options => {
          this.options$.next(options);
          this.emitSelectedOption(this.control.value as number | null);
        },
        error: () => {
          this.options$.next([]);
          this.emitSelectedOption(this.control.value as number | null);
        },
      });
  }

  private validateAndSanitizeSearchTerm(searchTerm: string): string | null {
    const sanitizedSearchTerm = searchTerm.trim().replaceAll(/\s+/g, ' ');

    if (!sanitizedSearchTerm) {
      return '';
    }

    const validationResult =
      this.securityService.validateInput(sanitizedSearchTerm);

    if (!validationResult.isValid) {
      this.options$.next([]);
      this.loaderService.hide();
      return null;
    }

    return sanitizedSearchTerm;
  }

  private setupControlValueSubscription(): void {
    this.control.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        this.emitSelectedOption(value as number | null);
      });
  }

  private emitSelectedOption(selectedId: number | null): void {
    if (!selectedId) {
      this.optionSelected.emit(null);
      return;
    }

    const selectedOption = this.options$.value.find(
      option => option.id === Number(selectedId)
    );

    this.optionSelected.emit(selectedOption ?? null);
  }
}
